/**
 * API客户端工具类
 * 统一附加 Access Token；401/410 触发会话失效处理（无 Refresh 重试）
 */
import { logger } from './logger';
import { secureTokenStorage } from './secureTokenStorage';

const REQUEST_TIMEOUT = 30000;
const UPLOAD_TIMEOUT = 300000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const AUTH_SKIP_ENDPOINTS = ['/users/login', '/users/register', '/users/password/forgot', '/admin/login'];

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  skip401Handler?: boolean;
}

interface ApiHttpError extends Error {
  status?: number;
  data?: unknown;
  responseText?: string;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('请求超时，请检查网络连接后重试');
    }
    throw error;
  }
}

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  retryDelay: number = RETRY_DELAY
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === maxRetries) {
        throw lastError;
      }

      const status = (lastError as ApiHttpError).status;
      if (status === 401 || status === 410) {
        throw lastError;
      }

      if (!shouldRetryRequest(lastError)) {
        throw lastError;
      }

      const delayTime = retryDelay * Math.pow(2, attempt);
      logger.debug(`请求失败，${delayTime}ms后重试 (${attempt + 1}/${maxRetries})`);
      await delay(delayTime);
    }
  }

  throw lastError || new Error('请求失败');
}

function shouldRetryRequest(error: Error): boolean {
  const errorMessage = error.message.toLowerCase();

  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('timeout')) {
    return true;
  }

  if (errorMessage.includes('status: 500') || errorMessage.includes('status: 502') || errorMessage.includes('status: 503')) {
    return true;
  }

  return false;
}

function shouldSkipAuth(endpoint: string, options?: ApiRequestOptions): boolean {
  if (options?.skipAuth) {
    return true;
  }
  return AUTH_SKIP_ENDPOINTS.some((path) => endpoint.includes(path));
}

function createHttpError(response: Response, responseText: string, errorData: unknown): ApiHttpError {
  const isProduction = process.env.NODE_ENV === 'production';
  const errorMessage = isProduction
    ? ((errorData as { message?: string })?.message || '请求失败')
    : ((errorData as { message?: string })?.message || `HTTP error! status: ${response.status}`);

  const error = new Error(errorMessage) as ApiHttpError;
  error.status = response.status;
  if (!isProduction) {
    error.data = errorData;
    error.responseText = responseText;
  }
  return error;
}

async function handleAuthFailure(status: number, message: string | undefined, options?: ApiRequestOptions): Promise<void> {
  if (options?.skip401Handler) {
    return;
  }
  if (status !== 401 && status !== 410) {
    return;
  }

  const { authService } = await import('../services/authService');
  await authService.handleUnauthorized(message);
}

export class ApiClient {
  static getAuthToken(): string | null {
    return secureTokenStorage.getAccessToken();
  }

  static getDefaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /** @deprecated Access 会话模型无 Refresh，等同于 getAuthToken */
  static async getValidAuthToken(): Promise<string | null> {
    return this.getAuthToken();
  }

  /** @deprecated 无主动刷新逻辑 */
  static async getDefaultHeadersWithRefresh(): Promise<Record<string, string>> {
    return this.getDefaultHeaders();
  }

  static getBaseUrl(): string {
    const protocol = window.location.protocol;
    const host = window.location.hostname;
    const port = window.location.port;

    logger.debug('API客户端连接信息:', { host, protocol, port });

    if (port === '12346') {
      return 'http://localhost:12345/api';
    }

    if (port === '443' || port === '8443' || port === '') {
      return `${protocol}//${host}${port ? `:${port}` : ''}/api`;
    }

    if (port === '12345') {
      return `${protocol}//${host}/api`;
    }

    return `${protocol}//${host}/api`;
  }

  private static buildHeaders(endpoint: string, options?: ApiRequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (!shouldSkipAuth(endpoint, options)) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return {
      ...headers,
      ...(options?.headers as Record<string, string> | undefined),
    };
  }

  private static async request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;

    const execute = async (): Promise<T> => {
      const headers = this.buildHeaders(endpoint, options);

      logger.debug(`${method}请求:`, url, body);

      const response = await fetchWithTimeout(
        url,
        {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          ...options,
        },
        REQUEST_TIMEOUT
      );

      const responseText = await response.text();

      if (!response.ok) {
        logger.error('HTTP错误响应:', responseText);

        let errorData: unknown = null;
        try {
          errorData = responseText ? JSON.parse(responseText) : null;
        } catch {
          // ignore
        }

        const httpError = createHttpError(response, responseText, errorData);

        if (response.status === 401 || response.status === 410) {
          const serverMessage = (errorData as { message?: string })?.message;
          await handleAuthFailure(response.status, serverMessage, options);
        }

        throw httpError;
      }

      const data = responseText ? JSON.parse(responseText) : null;
      return data as T;
    };

    return fetchWithRetry(execute);
  }

  static async get<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  static async post<T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>('POST', endpoint, data, options);
  }

  static async put<T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  static async patch<T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  static async delete<T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>('DELETE', endpoint, data, options);
  }

  /** multipart/form-data 上传；勿设置 Content-Type，由浏览器附带 boundary */
  static async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: ApiRequestOptions & { timeout?: number },
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const timeout = options?.timeout ?? UPLOAD_TIMEOUT;

    const execute = async (): Promise<T> => {
      const headers: Record<string, string> = {};

      if (!shouldSkipAuth(endpoint, options)) {
        const token = this.getAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      if (options?.headers) {
        Object.assign(headers, options.headers as Record<string, string>);
      }

      logger.debug('POST FormData 请求:', url);

      const response = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers,
          body: formData,
          ...options,
        },
        timeout,
      );

      const responseText = await response.text();

      if (!response.ok) {
        logger.error('HTTP错误响应:', responseText);

        let errorData: unknown = null;
        try {
          errorData = responseText ? JSON.parse(responseText) : null;
        } catch {
          // ignore
        }

        const httpError = createHttpError(response, responseText, errorData);

        if (response.status === 401 || response.status === 410) {
          const serverMessage = (errorData as { message?: string })?.message;
          await handleAuthFailure(response.status, serverMessage, options);
        }

        throw httpError;
      }

      const data = responseText ? JSON.parse(responseText) : null;
      return data as T;
    };

    return fetchWithRetry(execute);
  }
}
